"use client";

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../cn";
import { useFormStatus } from "react-dom";
import type { TestIdProps } from "./types";
import { TamiLoadingIndicator } from "../tami-loading-indicator/tami-loading-indicator.web";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/80",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/60",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:opacity-60",
        link: "text-foreground underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants>,
    TestIdProps {
  asChild?: boolean;
  /**
   * Ação em andamento (TAM-282): o botão fica cinza, o loader compacto entra
   * no lugar do texto — que segue ali, invisível, segurando a largura —, marca
   * `aria-busy` e não aceita clique. É o único jeito de mostrar carregamento
   * em botão; não repita a condição em `disabled`.
   */
  loading?: boolean;
}

/**
 * Cinza do carregamento: o mesmo em todas as variantes. Repetido no `disabled:`
 * para vencer o estilo de desabilitado que quem chama passe no `className`.
 */
const LOADING_CLASSES =
  "border-transparent bg-muted text-muted-foreground hover:bg-muted hover:opacity-100 disabled:border-transparent disabled:bg-muted disabled:text-muted-foreground disabled:opacity-100 disabled:brightness-100";

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading, disabled, testId, ...props }, ref) => {
    const { pending } = useFormStatus();
    const Comp = asChild ? Slot : "button";
    // O envio de formulário (`useFormStatus`) é carregamento como outro qualquer.
    const busy = Boolean(loading) || (pending && props.type === "submit");

    if (busy) {
      props.children = (
        <span className="relative inline-flex items-center justify-center">
          <span className="invisible inline-flex items-center justify-center">{props.children}</span>
          <TamiLoadingIndicator variant="compact" className="absolute h-4 w-4" />
        </span>
      );
    }

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }), busy && LOADING_CLASSES)}
        disabled={busy || disabled}
        aria-busy={busy || undefined}
        ref={ref}
        data-testid={testId}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
